import React from 'react';
import {
    Box, Flex, HStack, Icon, SimpleGrid, Text, VStack, useRadio, useRadioGroup,
} from '@chakra-ui/react';
import { FaBolt, FaClock, FaCheckCircle } from 'react-icons/fa';
import { MAXIMO_LALAMOVE, MINIMO_LALAMOVE, MODOS, brl, linhaDoTempo, pct } from './regras';

const ICONES = { normal: FaClock, prioridade: FaBolt };

function CartaoModo({ modo, regra, original, ...radioProps }) {
    const { getInputProps, getRadioProps, state } = useRadio(radioProps);
    const marcado = state.isChecked;
    const { titulo, subtitulo, cor } = MODOS[modo];

    // Tudo pela linha do tempo efetiva: o que a Lalamove vai receber de fato,
    // ja com o minimo de R$ 3 e o maximo de R$ 40.
    const pontos = linhaDoTempo(original, regra, 1000);
    const inicial = pontos[0]?.gorjeta || 0;
    const primeiro = pontos[1] || null;
    const ultimo = pontos[pontos.length - 1];
    const sobe = pontos.length > 1;

    return (
        <Box as="label" cursor="pointer" h="100%">
            <input {...getInputProps()} />
            <Box
                {...getRadioProps()}
                h="100%"
                borderWidth="2px"
                borderRadius="xl"
                p={{ base: 3, md: 4 }}
                minH="44px"
                transition="all 0.15s"
                borderColor={marcado ? `${cor}.400` : 'gray.200'}
                bg={marcado ? `${cor}.50` : 'white'}
                boxShadow={marcado ? 'md' : 'none'}
                _hover={{ borderColor: marcado ? `${cor}.400` : `${cor}.200` }}
                _focusVisible={{ boxShadow: 'outline' }}
            >
                <Flex justify="space-between" align="center" mb={2}>
                    <HStack spacing={2}>
                        <Flex
                            boxSize="32px"
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg={marcado ? `${cor}.500` : 'gray.100'}
                            color={marcado ? 'white' : 'gray.500'}
                            flexShrink={0}
                        >
                            <Icon as={ICONES[modo]} boxSize={4} />
                        </Flex>
                        <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" fontSize="md" lineHeight="1.2">{titulo}</Text>
                            <Text fontSize="xs" color="gray.500">{subtitulo}</Text>
                        </VStack>
                    </HStack>
                    <Icon
                        as={FaCheckCircle}
                        boxSize={5}
                        color={marcado ? `${cor}.500` : 'gray.200'}
                        aria-hidden
                    />
                </Flex>

                <VStack align="stretch" spacing={1} fontSize="sm">
                    <Flex justify="space-between" gap={2}>
                        <Text color="gray.600">Começa em</Text>
                        <Text fontWeight="bold" textAlign="right">
                            {brl(original + inicial)}
                            {inicial > 0 && (
                                <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500">
                                    {pontos[0].ajuste === 'minimo' ? ' (mín. Lalamove)' : ` (+${pct(pontos[0].percentual)})`}
                                </Text>
                            )}
                        </Text>
                    </Flex>
                    {sobe ? (
                        <>
                            <Flex justify="space-between" gap={2}>
                                <Text color="gray.600">1º aumento</Text>
                                <Text textAlign="right">{brl(primeiro.total)} em {primeiro.minuto} min</Text>
                            </Flex>
                            <Flex justify="space-between" gap={2}>
                                <Text color="gray.600">No máximo</Text>
                                <Text fontWeight="bold" textAlign="right">
                                    {brl(ultimo.total)}
                                    <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500"> em {ultimo.minuto} min</Text>
                                </Text>
                            </Flex>
                        </>
                    ) : (
                        <Text fontSize="xs" color="gray.500">O preço não sobe com o tempo.</Text>
                    )}
                </VStack>
            </Box>
        </Box>
    );
}

/**
 * Escolha do modo de aumento da gorjeta na hora de confirmar a corrida.
 *
 * Os dois modos seguem a mesma logica; muda a agressividade. Cada cartao ja
 * mostra em reais quanto a corrida custa agora e ate quanto pode chegar, para
 * o operador decidir olhando dinheiro, nao percentual.
 */
export default function EscolhaPrioridade({ regras, original, prioridade, onChange, isDisabled = false }) {
    const { getRootProps, getRadioProps } = useRadioGroup({
        name: 'modo-gorjeta',
        value: prioridade ? 'prioridade' : 'normal',
        onChange: (v) => onChange(v === 'prioridade'),
        isDisabled,
    });

    if (!regras || !(original > 0)) return null;

    return (
        <Box>
            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>Se nenhum motorista aceitar</Text>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} {...getRootProps()} role="radiogroup" aria-label="Modo de aumento da gorjeta">
                {['normal', 'prioridade'].map((modo) => (
                    <CartaoModo
                        key={modo}
                        modo={modo}
                        regra={regras[modo]}
                        original={original}
                        {...getRadioProps({ value: modo })}
                    />
                ))}
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" mt={2}>
                A gorjeta sobe sozinha enquanto a Lalamove procura motorista e para assim que alguém aceita.
                Dá para parar ou adiantar os aumentos depois, na tela da corrida. A Lalamove aceita gorjeta
                de {brl(MINIMO_LALAMOVE)} a {brl(MAXIMO_LALAMOVE)}.
            </Text>
        </Box>
    );
}
